import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ModelViewer from '../components/ModelViewer';
import Header from '../components/Header';
import ShareModal from '../components/ShareModal';
import ManageSharesModal from '../components/ManageSharesModal';
import { filesApi } from '../utils/api';
import { formatFileSize, formatDate, downloadBlob, getFileTypeLabel, is3DModel } from '../utils/helpers';
import * as pdfjsLib from 'pdfjs-dist';
import mammoth from 'mammoth';
import * as XLSX from 'xlsx';
import {
  ArrowLeft, Download, FileText, Table, Image, File,
  ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Save, Edit2,
  Lock, Shield, CheckCircle, Share2, Link, Settings, Video
} from 'lucide-react';

// Set up PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

const FileViewerPage = () => {
  const { projectId, fileId } = useParams();
  const navigate = useNavigate();

  const [file, setFile] = useState(null);
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [notes, setNotes] = useState('');
  const [editingNotes, setEditingNotes] = useState(false);
  const [savingNotes, setSavingNotes] = useState(false);

  // Download progress state
  const [downloading, setDownloading] = useState(false);
  const [downloadStep, setDownloadStep] = useState(0); // 0: none, 1: decrypt, 2: prepare, 3: download

  // Share modal state
  const [showShareModal, setShowShareModal] = useState(false);
  const [showManageSharesModal, setShowManageSharesModal] = useState(false);

  // PDF state
  const [pdfDoc, setPdfDoc] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [scale, setScale] = useState(1.0);
  const canvasRef = useRef(null);

  // Excel state
  const [excelData, setExcelData] = useState(null);
  const [activeSheet, setActiveSheet] = useState(0);

  useEffect(() => {
    loadFile();
  }, [fileId]);

  useEffect(() => {
    if (pdfDoc && canvasRef.current) {
      renderPage(currentPage);
    }
  }, [pdfDoc, currentPage, scale]);

  const loadFile = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load file metadata
      const metaResponse = await filesApi.getById(fileId);
      setFile(metaResponse.data);
      setNotes(metaResponse.data.notes || '');

      // Load file content
      const contentResponse = await filesApi.getContent(fileId);
      const blob = contentResponse.data;

      // Process based on file type
      if (metaResponse.data.mime_type === 'application/pdf') {
        await loadPdf(blob);
      } else if (
        metaResponse.data.mime_type ===
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      ) {
        await loadDocx(blob);
      } else if (
        metaResponse.data.mime_type ===
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
        metaResponse.data.mime_type === 'application/vnd.ms-excel'
      ) {
        await loadExcel(blob);
      } else if (metaResponse.data.mime_type.startsWith('image/')) {
        const url = URL.createObjectURL(blob);
        setContent({ type: 'image', url });
      } else if (metaResponse.data.mime_type.startsWith('video/')) {
        const url = URL.createObjectURL(blob);
        setContent({ type: 'video', url });
      } else if (is3DModel(metaResponse.data.file_extension)) {
        setContent({ type: 'model', blob, extension: metaResponse.data.file_extension });
      } else {
        setError('This file type cannot be displayed');
      }
    } catch (err) {
      console.error('Load file error:', err);
      setError('Failed to load file');
    } finally {
      setLoading(false);
    }
  };

  const loadPdf = async (blob) => {
    try {
      const arrayBuffer = await blob.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      setPdfDoc(pdf);
      setTotalPages(pdf.numPages);
      setContent({ type: 'pdf' });
    } catch (err) {
      console.error('PDF load error:', err);
      setError('Failed to load PDF');
    }
  };

  const renderPage = async (pageNum) => {
    if (!pdfDoc || !canvasRef.current) return;

    try {
      const page = await pdfDoc.getPage(pageNum);
      const viewport = page.getViewport({ scale });
      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');

      canvas.height = viewport.height;
      canvas.width = viewport.width;

      await page.render({
        canvasContext: context,
        viewport: viewport
      }).promise;
    } catch (err) {
      console.error('Render page error:', err);
    }
  };

  const loadDocx = async (blob) => {
    try {
      const arrayBuffer = await blob.arrayBuffer();
      const result = await mammoth.convertToHtml({ arrayBuffer });
      setContent({ type: 'docx', html: result.value });
    } catch (err) {
      console.error('DOCX load error:', err);
      setError('Failed to load document');
    }
  };

  const loadExcel = async (blob) => {
    try {
      const arrayBuffer = await blob.arrayBuffer();
      const workbook = XLSX.read(arrayBuffer, { type: 'array' });

      const sheets = workbook.SheetNames.map((name) => {
        const worksheet = workbook.Sheets[name];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
        return { name, data: jsonData };
      });

      setExcelData(sheets);
      setContent({ type: 'excel' });
    } catch (err) {
      console.error('Excel load error:', err);
      setError('Failed to load spreadsheet');
    }
  };

  const handleDownload = async () => {
    try {
      setDownloading(true);

      // Step 1: Decrypting
      setDownloadStep(1);
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Step 2: Preparing
      setDownloadStep(2);
      const response = await filesApi.download(fileId);
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Step 3: Downloading
      setDownloadStep(3);
      await new Promise(resolve => setTimeout(resolve, 1000));

      downloadBlob(response.data, file.original_name);
    } catch (err) {
      console.error('Download error:', err);
    } finally {
      setDownloading(false);
      setDownloadStep(0);
    }
  };

  const handleSaveNotes = async () => {
    try {
      setSavingNotes(true);
      await filesApi.updateNotes(fileId, notes);
      setEditingNotes(false);
    } catch (err) {
      console.error('Save notes error:', err);
    } finally {
      setSavingNotes(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col">
      <Header />

      {/* Toolbar */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-3">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(`/project/${projectId}`)}
              className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="hidden sm:inline">Back</span>
            </button>

            {file && (
              <div className="flex items-center gap-3">
                <span className="font-medium text-gray-900 dark:text-white truncate max-w-[200px] sm:max-w-none">
                  {file.original_name}
                </span>
                <span className="text-sm text-gray-500 dark:text-gray-400 hidden sm:inline">
                  {getFileTypeLabel(file.file_extension)}
                </span>
                {/* Encryption indicator */}
                <span className="flex items-center gap-1 px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-full text-xs font-medium">
                  <Lock className="w-3 h-3" />
                  <span className="hidden sm:inline">AES-256 encrypted</span>
                </span>
              </div>
            )}
          </div>

          <div className="flex items-center gap-2">
            {/* PDF controls */}
            {content?.type === 'pdf' && (
              <>
                <button
                  onClick={() => setScale((s) => Math.max(0.5, s - 0.25))}
                  className="p-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                  title="Zoom out"
                >
                  <ZoomOut className="w-5 h-5" />
                </button>
                <span className="text-sm text-gray-600 dark:text-gray-400 min-w-[60px] text-center">
                  {Math.round(scale * 100)}%
                </span>
                <button
                  onClick={() => setScale((s) => Math.min(3, s + 0.25))}
                  className="p-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                  title="Zoom in"
                >
                  <ZoomIn className="w-5 h-5" />
                </button>
                <div className="w-px h-6 bg-gray-300 dark:bg-gray-600 mx-2" />
              </>
            )}

            <button
              onClick={() => setShowShareModal(true)}
              className="btn btn-secondary flex items-center gap-2"
            >
              <Share2 className="w-5 h-5" />
              <span className="hidden sm:inline">Share</span>
            </button>

            <button
              onClick={handleDownload}
              disabled={downloading}
              className="btn btn-primary flex items-center gap-2"
            >
              <Download className="w-5 h-5" />
              <span className="hidden sm:inline">Download</span>
            </button>
          </div>
        </div>

        {/* Download Progress Overlay */}
        {downloading && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-2xl max-w-sm w-full mx-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 text-center">
                Downloading file
              </h3>

              <div className="space-y-4">
                {/* Step 1: Decrypt */}
                <div className={`flex items-center gap-3 p-3 rounded-lg transition-all ${
                  downloadStep >= 1
                    ? 'bg-green-50 dark:bg-green-900/20'
                    : 'bg-gray-50 dark:bg-gray-700/50'
                }`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    downloadStep > 1
                      ? 'bg-green-500 text-white'
                      : downloadStep === 1
                        ? 'bg-primary-500 text-white animate-pulse'
                        : 'bg-gray-200 dark:bg-gray-600 text-gray-500'
                  }`}>
                    {downloadStep > 1 ? (
                      <CheckCircle className="w-5 h-5" />
                    ) : (
                      <Lock className="w-4 h-4" />
                    )}
                  </div>
                  <div className="flex-1">
                    <p className={`font-medium ${
                      downloadStep >= 1 ? 'text-gray-900 dark:text-white' : 'text-gray-500'
                    }`}>
                      Decoding
                    </p>
                    <p className="text-xs text-gray-500">AES-256 encrypted file</p>
                  </div>
                  {downloadStep === 1 && (
                    <div className="w-5 h-5 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
                  )}
                </div>

                {/* Step 2: Prepare */}
                <div className={`flex items-center gap-3 p-3 rounded-lg transition-all ${
                  downloadStep >= 2
                    ? 'bg-green-50 dark:bg-green-900/20'
                    : 'bg-gray-50 dark:bg-gray-700/50'
                }`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    downloadStep > 2
                      ? 'bg-green-500 text-white'
                      : downloadStep === 2
                        ? 'bg-primary-500 text-white animate-pulse'
                        : 'bg-gray-200 dark:bg-gray-600 text-gray-500'
                  }`}>
                    {downloadStep > 2 ? (
                      <CheckCircle className="w-5 h-5" />
                    ) : (
                      <Shield className="w-4 h-4" />
                    )}
                  </div>
                  <div className="flex-1">
                    <p className={`font-medium ${
                      downloadStep >= 2 ? 'text-gray-900 dark:text-white' : 'text-gray-500'
                    }`}>
                      Preparing file
                    </p>
                    <p className="text-xs text-gray-500">Verifying file</p>
                  </div>
                  {downloadStep === 2 && (
                    <div className="w-5 h-5 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
                  )}
                </div>

                {/* Step 3: Download */}
                <div className={`flex items-center gap-3 p-3 rounded-lg transition-all ${
                  downloadStep >= 3
                    ? 'bg-green-50 dark:bg-green-900/20'
                    : 'bg-gray-50 dark:bg-gray-700/50'
                }`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    downloadStep > 3
                      ? 'bg-green-500 text-white'
                      : downloadStep === 3
                        ? 'bg-primary-500 text-white animate-pulse'
                        : 'bg-gray-200 dark:bg-gray-600 text-gray-500'
                  }`}>
                    {downloadStep > 3 ? (
                      <CheckCircle className="w-5 h-5" />
                    ) : (
                      <Download className="w-4 h-4" />
                    )}
                  </div>
                  <div className="flex-1">
                    <p className={`font-medium ${
                      downloadStep >= 3 ? 'text-gray-900 dark:text-white' : 'text-gray-500'
                    }`}>
                      Downloading...
                    </p>
                    <p className="text-xs text-gray-500">Saving file</p>
                  </div>
                  {downloadStep === 3 && (
                    <div className="w-5 h-5 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col lg:flex-row overflow-hidden">
        {/* Viewer */}
        <div className="flex-1 overflow-auto p-4 lg:p-8">
          {loading && (
            <div className="flex items-center justify-center h-full">
              <div className="spinner"></div>
            </div>
          )}

          {error && (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <File className="w-16 h-16 text-gray-400 mb-4" />
              <p className="text-gray-600 dark:text-gray-400">{error}</p>
            </div>
          )}

          {/* PDF Viewer */}
          {content?.type === 'pdf' && (
            <div className="flex flex-col items-center">
              <canvas
                ref={canvasRef}
                className="shadow-lg max-w-full"
              />

              {/* Page navigation */}
              <div className="flex items-center gap-4 mt-4 p-3 bg-white dark:bg-gray-800 rounded-lg shadow">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage <= 1}
                  className="p-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg disabled:opacity-50"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {currentPage} / {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage >= totalPages}
                  className="p-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg disabled:opacity-50"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          )}

          {/* DOCX Viewer */}
          {content?.type === 'docx' && (
            <div className="max-w-4xl mx-auto bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8">
              <div
                className="prose dark:prose-invert max-w-none"
                dangerouslySetInnerHTML={{ __html: content.html }}
              />
            </div>
          )}

          {/* Excel Viewer */}
          {content?.type === 'excel' && excelData && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
              {/* Sheet tabs */}
              {excelData.length > 1 && (
                <div className="flex border-b border-gray-200 dark:border-gray-700 overflow-x-auto">
                  {excelData.map((sheet, index) => (
                    <button
                      key={index}
                      onClick={() => setActiveSheet(index)}
                      className={`px-4 py-2 text-sm font-medium whitespace-nowrap ${
                        activeSheet === index
                          ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 border-b-2 border-primary-600'
                          : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                      }`}
                    >
                      {sheet.name}
                    </button>
                  ))}
                </div>
              )}

              {/* Table */}
              <div className="overflow-auto max-h-[600px]">
                <table className="w-full border-collapse">
                  <tbody>
                    {excelData[activeSheet]?.data.map((row, rowIndex) => (
                      <tr key={rowIndex} className={rowIndex === 0 ? 'bg-gray-50 dark:bg-gray-700' : ''}>
                        {row.map((cell, cellIndex) => (
                          <td
                            key={cellIndex}
                            className={`border border-gray-200 dark:border-gray-600 px-3 py-2 text-sm ${
                              rowIndex === 0 ? 'font-semibold' : ''
                            }`}
                          >
                            {cell}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Image Viewer */}
          {content?.type === 'image' && (
            <div className="flex items-center justify-center">
              <img
                src={content.url}
                alt={file?.original_name}
                className="max-w-full max-h-[80vh] object-contain shadow-lg rounded-lg"
              />
            </div>
          )}

          {/* Video Viewer */}
          {content?.type === 'video' && (
            <div className="flex items-center justify-center">
              <video
                src={content.url}
                controls
                className="max-w-full max-h-[80vh] shadow-lg rounded-lg"
              >
                Your browser does not support video playback.
              </video>
            </div>
          )}

          {/* 3D Model Viewer */}
          {content?.type === 'model' && (
            <ModelViewer blob={content.blob} extension={content.extension} />
          )}
        </div>

        {/* Sidebar with file info and notes */}
        <div className="w-full lg:w-80 bg-white dark:bg-gray-800 border-t lg:border-t-0 lg:border-l border-gray-200 dark:border-gray-700 p-4 lg:p-6 overflow-y-auto">
          {file && (
            <>
              {/* File info */}
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
                  File information
                </h3>
                <dl className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <dt className="text-gray-500 dark:text-gray-400">Type</dt>
                    <dd className="text-gray-900 dark:text-white">
                      {getFileTypeLabel(file.file_extension)}
                    </dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-gray-500 dark:text-gray-400">Size</dt>
                    <dd className="text-gray-900 dark:text-white">
                      {formatFileSize(file.file_size)}
                    </dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-gray-500 dark:text-gray-400">Uploaded</dt>
                    <dd className="text-gray-900 dark:text-white">
                      {formatDate(file.created_at)}
                    </dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-gray-500 dark:text-gray-400">Uploaded by</dt>
                    <dd className="text-gray-900 dark:text-white truncate max-w-[150px]">
                      {file.uploaded_by_name}
                    </dd>
                  </div>
                  <div className="flex justify-between items-center pt-2 border-t border-gray-100 dark:border-gray-700 mt-2">
                    <dt className="text-gray-500 dark:text-gray-400">Encryption</dt>
                    <dd className="flex items-center gap-1 text-green-600 dark:text-green-400">
                      <Lock className="w-4 h-4" />
                      <span>AES-256</span>
                    </dd>
                  </div>
                </dl>
              </div>

              {/* Sharing */}
              <div className="mb-6 pb-6 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
                  Sharing
                </h3>
                <div className="space-y-2">
                  <button
                    onClick={() => setShowShareModal(true)}
                    className="w-full flex items-center gap-2 p-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                  >
                    <Link className="w-4 h-4" />
                    Create share link
                  </button>
                  <button
                    onClick={() => setShowManageSharesModal(true)}
                    className="w-full flex items-center gap-2 p-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                  >
                    <Settings className="w-4 h-4" />
                    Manage shares
                  </button>
                </div>
              </div>

              {/* Notes */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                    Notes
                  </h3>
                  {!editingNotes && (
                    <button
                      onClick={() => setEditingNotes(true)}
                      className="p-1 text-gray-500 hover:text-primary-600"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                  )}
                </div>

                {editingNotes ? (
                  <div>
                    <textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      className="input min-h-[150px] text-sm"
                      placeholder="Add a note..."
                    />
                    <div className="flex justify-end gap-2 mt-2">
                      <button
                        onClick={() => {
                          setNotes(file.notes || '');
                          setEditingNotes(false);
                        }}
                        className="btn btn-secondary text-sm py-1 px-3"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleSaveNotes}
                        disabled={savingNotes}
                        className="btn btn-primary text-sm py-1 px-3 flex items-center gap-1"
                      >
                        <Save className="w-4 h-4" />
                        {savingNotes ? 'Saving...' : 'Save'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-gray-600 dark:text-gray-400 whitespace-pre-wrap">
                    {notes || 'No notes'}
                  </p>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Share Modal */}
      <ShareModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        file={file}
      />

      {/* Manage Shares Modal */}
      <ManageSharesModal
        isOpen={showManageSharesModal}
        onClose={() => setShowManageSharesModal(false)}
        file={file}
      />
    </div>
  );
};

export default FileViewerPage;
